import { CameraAnimation } from "@/components/r3f/animations/camera-animation";
import { SuspenseLoadNotifier } from "@/components/r3f/utils/suspense-load-notifier";
import type { SceneFilter } from "@/types/scene-filter";
import { ForwardMovement } from "@faro-lotv/lotv";
import {
  Suspense,
  useImperativeHandle,
  useRef,
  useState,
  type JSX,
  type RefObject,
} from "react";
import { useCurrentArea } from "../mode-data-context";
import type { CurrentScene } from "../mode-selectors";
import type { InitialAnimationInfo } from "./data-dispatch/flywalk-animation";
import {
  FlyWalkDataDispatch,
  INITIAL_DATA_DISPATCH_RESULT,
  type DataDispatchResult,
} from "./data-dispatch/flywalk-data-dispatch";
import { useDowncastObjects } from "./data-dispatch/flywalk-renderables";
import {
  INITIAL_FLYWALK_ANIMATION_DATA,
  type FlyWalkAnimationData,
} from "./data-dispatch/use-flywalk-animation";
import { FlyWalkControls } from "./flywalk-controls";
import { useOnPanoClicked, type OnWaypointClicked } from "./flywalk-scene";
import type { FlyWalkWaypoint } from "./flywalk-types";
import { FlyWalkRenderers } from "./renderers/flywalk-renderers";
import { FlyWalkRenderingPipeline } from "./rendering-pipeline/flywalk-rendering-pipeline";
import type { SingleViewCameras } from "./use-fly-split-scene-cameras";
import {
  useOrthoCameraForImagesSplitView,
  usePerspectiveCameraForNonImagesSplitView,
} from "./use-orthocamera-for-images";

export type FlySplitViewActions = {
  /** Callback issued when a waypoint is clicked in the minimap */
  onPanoClickedInMinimap: OnWaypointClicked;
};

type FlySplitViewProps = {
  /** The scene to render in this view */
  currentScene: CurrentScene;
  /** The scene filter to use for this view */
  sceneFilter: SceneFilter;
  /** Whether this view is the secondary view (right side) or not (left side) */
  isSecondaryView: boolean;
  /** Callback issued when a waypoint is clicked */
  onWaypointClicked(waypoint: FlyWalkWaypoint): void;
  /** A handle on actions that can be executed on the split view */
  actions?: RefObject<FlySplitViewActions | null>;
  /** The cameras used to render this view*/
  cameras: SingleViewCameras;
};

/** @returns a component suitable to render one of the two views shown in splitscreen mode */
export function FlySplitView({
  currentScene,
  sceneFilter,
  isSecondaryView,
  onWaypointClicked,
  actions,
  cameras,
}: FlySplitViewProps): JSX.Element {
  const area = useCurrentArea();

  const [dataDispatchResult, setDataDispatchResult] =
    useState<DataDispatchResult>(INITIAL_DATA_DISPATCH_RESULT);

  const [animationData, setAnimationData] = useState<FlyWalkAnimationData>(
    INITIAL_FLYWALK_ANIMATION_DATA,
  );

  // 'dataDispatchResult' contains the project data that should be dispatched to this scene for rendering,
  // according to the user selection and the scene filter
  const {
    main,
    mainObject,
    overlayObject,
    annotations,
    measurements,
    imageType,
  } = dataDispatchResult;

  usePerspectiveCameraForNonImagesSplitView(main, cameras);

  // 'animationData' contains the current animation state and the previous main object for rendering.
  // Notably, the 'currentSceneFilter' below is different from the 'flyWalkSceneFilter' above.
  // The latter refers to what the user has selected in the UI. The former refers to what the
  // rendering pipeline is ready to take as input (as opposed to an animation)
  const {
    flyWalkAnimation,
    currentSceneFilter,
    previousMain,
    previousMainObject,
    endAnimation,
    moveCameraTo,
  } = animationData;

  useOrthoCameraForImagesSplitView(
    main,
    flyWalkAnimation,
    !previousMain,
    cameras,
  );

  // Retrieves the current renderable objects with their correct types.
  const renderables = useDowncastObjects(
    mainObject,
    overlayObject,
    previousMainObject,
    currentSceneFilter,
    currentScene.activeSheets,
    isSecondaryView,
  );

  const initialAnimationInfo = useRef<InitialAnimationInfo | null>({
    canSkipInitialTransition: true,
  });

  const { onPanoClicked, onPanoClickedInMinimap } = useOnPanoClicked(
    currentSceneFilter,
    onWaypointClicked,
    moveCameraTo,
    currentScene.activeSheetFor2DNavigation,
  );

  useImperativeHandle(actions, () => ({
    onPanoClickedInMinimap,
  }));

  return (
    <>
      <Suspense
        fallback={<SuspenseLoadNotifier withSnapshotRenderer={false} />}
      >
        <FlyWalkDataDispatch
          flyWalkSceneFilter={sceneFilter}
          currentScene={currentScene}
          currentArea={area}
          initialAnimationInfo={initialAnimationInfo}
          setDataDispatchResult={setDataDispatchResult}
          setAnimationData={setAnimationData}
          defaultOrthoCamera={cameras.orthoCamera}
        />
      </Suspense>
      <FlyWalkRenderers
        renderables={renderables}
        sheets={[]}
        panos={currentScene.panos}
        paths={currentScene.paths}
        sheetForElevation={currentScene.activeSheetFor2DNavigation}
        annotations={annotations}
        measurements={measurements}
        animation={flyWalkAnimation?.renderingAnimation ?? null}
        sceneFilter={currentSceneFilter}
        imageType={imageType}
        isOverview={false}
        onPanoClicked={onPanoClicked}
        onWalkToPosition={flyWalkAnimation ? undefined : moveCameraTo}
      />
      <FlyWalkControls
        imageType={imageType}
        animation={flyWalkAnimation}
        target={undefined}
        forwardMovement={ForwardMovement.Walk}
        clickableByControls={[]}
      />
      <FlyWalkRenderingPipeline
        pointCloud={renderables.pointCloud}
        cadModel={renderables.cadModel}
        sceneFilter={currentSceneFilter}
        animation={flyWalkAnimation?.renderingAnimation ?? null}
        endAnimation={endAnimation}
      />
      {flyWalkAnimation?.cameraAnimation && (
        <CameraAnimation
          {...flyWalkAnimation.cameraAnimation}
          onAnimationFinished={endAnimation}
        />
      )}
      {flyWalkAnimation?.targetCameraAnimation && (
        <CameraAnimation
          {...flyWalkAnimation.targetCameraAnimation}
          syncControlsWithCameraAtEnd={false}
        />
      )}
    </>
  );
}
